import { useState } from 'react';
import { Form } from 'react-admin';
import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';

import { claimPumpkinNowDecisionStyle } from './claim-pumpkin-now-decision-style';

export function getReasonsToDefer(isEstimate, isPaymentAtVet, modal) {
  let reasonsToDefer = [
    { id: 'full_mrr_required', name: 'Full MRR required' },
    { id: 'dol_mrr_required', name: 'DOL MRR required' },
    { id: 'another_open_claim', name: 'Another open claim' },
    { id: 'wellness_only_claim', name: 'Wellness only claim' },
    {
      id: 'inaccurate_documentation',
      name: 'Incomplete or wrong document',
    },
  ];
  if (!isEstimate) {
    reasonsToDefer = reasonsToDefer.concat([
      {
        id: 'invoice_less_than_limit',
        name: `Invoice less than ${isPaymentAtVet ? '500' : '1k'}`,
      },
      {
        id: 'multiple_pets_on_invoice',
        name: `Multiple pets on invoice (below ${
          isPaymentAtVet ? '500' : '1k'
        } for pet)`,
      },
      { id: 'multiple_visits', name: 'Multiple invoices/separate visits' },
    ]);
    if (modal) {
      reasonsToDefer.push({
        id: 'authority_limit_exceed',
        name: 'Exceeds carrier authority limit',
      });
    }
  }
  reasonsToDefer.push({ id: 'other', name: 'Other (custom field)' });
  return reasonsToDefer;
}

function getDeferralNote(isEstimate, isPaymentAtVet) {
  if (isEstimate) {
    return 'Submitted as an estimate (no $ threshold applies)';
  }
  return isPaymentAtVet
    ? 'Submitted as a claim to pay-at-vet (above $500 invoices acceptable)'
    : 'Submitted as a claim for reimbursement (above $1000 invoices acceptable)';
}

export const PUMPKIN_NOW_STATUSES = {
  PENDING: 'PENDING',
  EXPEDITED: 'EXPEDITED',
  DEFERRED: 'DEFERRED',
};

function ClaimPumpkinNowDecision({
  onSubmit = () => {},
  isSubmitting = false,
  pumpkinNowStatus = '',
  modal = false,
  isEstimate = false,
  isPaymentAtVet = false,
}) {
  const classes = claimPumpkinNowDecisionStyle({ modal });
  const [actionBtnValue, setActionBtnValue] = useState(null);
  const [deferralReason, setDeferralReason] = useState('');
  const [notes, setNotes] = useState('');

  const isConfirmButtonDisabled = !(
    actionBtnValue === PUMPKIN_NOW_STATUSES.EXPEDITED ||
    (actionBtnValue === PUMPKIN_NOW_STATUSES.DEFERRED &&
      deferralReason &&
      (deferralReason !== 'other' || (deferralReason === 'other' && !!notes)))
  );

  function handleOnButtonChange(event) {
    const { value } = event.target;
    if (value === PUMPKIN_NOW_STATUSES.EXPEDITED) {
      setDeferralReason('');
      setNotes('');
    }
    setActionBtnValue(value);
  }

  return (
    <Form onSubmit={() => onSubmit(actionBtnValue, deferralReason, notes)}>
      <Typography component='div' role='tabpanel' className={classes.contentWrapper}>
        <div className={classes.pumpkinNowTitle}>Pumpkin Now Decision</div>

        <Grid container spacing={1}>
          <Grid item xs={3}>
            <ToggleButtonGroup
              value={actionBtnValue}
              exclusive
              onChange={handleOnButtonChange}
              labelId='reimbursement-rate'
              size='large'
              className={classes.buttonGroup}
            >
              <ToggleButton
                className={classes.toggleButton}
                value={PUMPKIN_NOW_STATUSES.EXPEDITED}
                disabled={pumpkinNowStatus === PUMPKIN_NOW_STATUSES.EXPEDITED}
              >
                EXPEDITE
              </ToggleButton>
              <ToggleButton
                className={classes.toggleDeferButton}
                value={PUMPKIN_NOW_STATUSES.DEFERRED}
              >
                DEFER
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          <Grid item xs={3}>
            {actionBtnValue === PUMPKIN_NOW_STATUSES.DEFERRED ? (
              <FormControl
                fullWidth
                variant='outlined'
                margin='normal'
                className={classes.deferralSelect}
              >
                <InputLabel id='status-label'>Deferral Reason</InputLabel>
                <Select
                  labelId='status-label'
                  value={deferralReason}
                  onChange={e => setDeferralReason(e.target.value)}
                  label='Deferral Reason'
                >
                  {getReasonsToDefer(isEstimate, isPaymentAtVet, modal).map(
                    reason => (
                      <MenuItem key={reason.id} value={reason.id}>
                        {reason.name}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            ) : null}
          </Grid>

          <Grid item xs={4}>
            {actionBtnValue === PUMPKIN_NOW_STATUSES.DEFERRED ? (
              <TextField
                multiline
                label='Notes for deferral'
                source='notes'
                minRows={1}
                fullWidth
                variant='outlined'
                className={classes.notesTextInput}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                name={'notes'}
              />
            ) : null}
          </Grid>

          <Grid item xs={2}>
            <Grid container justifyContent='flex-end'>
              {isSubmitting ? (
                <CircularProgress size={30} />
              ) : (
                <Button
                  variant='contained'
                  color='primary'
                  size='large'
                  disabled={isConfirmButtonDisabled}
                  className={classes.confirmButton}
                  type='submit'
                >
                  Confirm
                </Button>
              )}
            </Grid>
          </Grid>
        </Grid>

        {actionBtnValue === PUMPKIN_NOW_STATUSES.DEFERRED ? (
          <Grid container spacing={2} className={classes.deferNote}>
            <Grid item className={classes.deferNoteText}>
              {getDeferralNote(isEstimate, isPaymentAtVet)}
            </Grid>
          </Grid>
        ) : null}
      </Typography>
    </Form>
  );
}

ClaimPumpkinNowDecision.propTypes = {
  isSubmitting: PropTypes.bool,
  onSubmit: PropTypes.func,
  pumpkinNowStatus: PropTypes.string,
  modal: PropTypes.bool,
  isEstimate: PropTypes.bool,
  isPaymentAtVet: PropTypes.bool,
};

export default ClaimPumpkinNowDecision;
